import { useContext, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { min } from 'lib/array/arrayUtils'
import { formatRoundedAmountWithDollarType } from 'lib/format/formatCurrencyIntl'
import type { DrawerExperienceSearchTile } from '../components/ExperienceListingDrawer/ExperienceDetailView'

type PricedExperienceTile = DrawerExperienceSearchTile & { price: number }

interface LowestExperiencePrice {
  experience: DrawerExperienceSearchTile | undefined
  formatted: string | undefined
}

export default function useLowestExperiencePrice(
  experiences: Array<DrawerExperienceSearchTile>,
): LowestExperiencePrice {
  const geo = useContext(GeoContext)
  const currentCurrency = useContext(CurrencyContext)

  /*
   * One currency only. `min` compares raw numbers, so a ¥900 experience would beat a $50 one and
   * then be formatted as $9. Anything quoted in another currency is left out rather than
   * mis-ranked -- the row drops the "from" clause and still says how many there are.
   */
  const experience = useMemo(
    () => min(
      experiences.filter((item): item is PricedExperienceTile =>
        item.price !== undefined && (item.currencyCode ?? currentCurrency) === currentCurrency),
      (item) => item.price,
    ),
    [currentCurrency, experiences],
  )

  const formatted = useMemo(() => {
    if (experience?.price === undefined) return undefined

    return formatRoundedAmountWithDollarType(
      experience.price,
      currentCurrency,
      geo.currentRegionCode,
    )
  }, [experience, currentCurrency, geo.currentRegionCode])

  return useMemo(() => ({ experience, formatted }), [experience, formatted])
}
